import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from 'fronton-react';
import { CheckCircleIcon } from '@fronton/icons-react';
import { selectPageModelStoreOperationalTime } from 'store/pageModel';
import {
	ReasonsContainer,
	SmallTextContainer,
	BenefitsContainer,
	captionStyles,
	scheduleStyles,
	selectedScheduleStyles,
	benefitsIconStyles,
} from './styles';

type SmallTextProps = {
	texts: string[];
};

export const SmallText = memo(({ texts }: SmallTextProps) => {
	if (!texts || !texts.length) return null;
	return (
		<SmallTextContainer data-qa="small-text">
			{texts.map(item => (
				<Typography
					as="p"
					size="body_long"
					variant="s"
					key={`smallText-${item}`}
					data-qa="small-text-item"
				>
					{item}
				</Typography>
			))}
		</SmallTextContainer>
	);
});

type ReasonProps = {
	reasons: {
		description: string;
		listItems: string[];
	} | null;
};

export const Reasons = memo(({ reasons }: ReasonProps) => {
	if (!reasons) return null;

	const { description, listItems } = reasons;
	return (
		<ReasonsContainer data-qa="reasons">
			<Typography size="body_long" variant="s">
				{description}
			</Typography>
			<ul>
				{listItems.map(item => (
					<li key={`reason-${item}`} data-qa="reasons-item">
						<Typography size="body_long" variant="s" key={`reasons-${item}`}>
							{item}
						</Typography>
					</li>
				))}
			</ul>
		</ReasonsContainer>
	);
});

type BenefitsProps = {
	benefits: {
		title: string;
		listItems: string[];
	} | null;
};

export const Benefits = memo(({ benefits }: BenefitsProps) => {
	if (!benefits) return null;

	const { title, listItems } = benefits;
	return (
		<BenefitsContainer data-qa="benefits">
			<Typography as="h3" variant="h3">
				{title}
			</Typography>
			<ul>
				{listItems.map(item => (
					<li key={`benefits-${item}`} data-qa="benefits-item">
						<CheckCircleIcon
							className={benefitsIconStyles}
							type="outline"
							size="l"
							color="brand-primary"
						/>
						<Typography size="body_long" variant="s" key={`reasons-${item}`}>
							{item}
						</Typography>
					</li>
				))}
			</ul>
		</BenefitsContainer>
	);
});

type CaptionProps = {
	text: string;
};

export const Caption = memo(({ text }: CaptionProps) => {
	if (!text) return null;

	return (
		<Typography
			variant="caption"
			as="p"
			color="text-secondary"
			className={captionStyles}
			data-qa="caption"
			overrideStyles={false}
		>
			{text}
		</Typography>
	);
});

type ScheduleProps = {
	text: string;
};

export const SelectedSchedule = ({ text }: ScheduleProps) => {
	const operationalTime = useSelector(selectPageModelStoreOperationalTime);

	if (!operationalTime && !text) return null;

	return (
		<Typography
			variant="caption_accent"
			className={selectedScheduleStyles}
			data-qa="selected-schedule"
			overrideStyles={false}
		>
			{operationalTime ? `График предоставления услуги в магазине: ${operationalTime}` : text}
		</Typography>
	);
};

export const DefaultSchedule = ({ text }: ScheduleProps) =>
	text ? (
		<Typography
			variant="s"
			size="body_accent"
			className={scheduleStyles}
			data-qa="default-schedule"
			overrideStyles={false}
		>
			{text}
		</Typography>
	) : null;
